// Каталог: направления, группы, уровни, категории, возрасты (выделено из reference.api.ts планом 108, Ф4).
import { api } from '@/lib/api';

import { archivedQuery, defined, type ListParams } from './_shared.js';

export interface Direction {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  isArchived: boolean;
  /** FK на категорию (Mobifitness «Направление»); null — без категории. */
  categoryId: string | null;
  /** Имя категории — подпись рядом с направлением при создании группы (план 103). */
  categoryName?: string | null;
  /** Перекрёстные связи: активные группы направления / педагоги с ним в профиле. */
  groupsCount: number;
  teachersCount: number;
  /** Филиалы направления (direction_branches). */
  branchIds: string[];
  /** Педагоги направления (employee_directions). */
  teacherIds: string[];
  /**
   * Закрытые («творческие») группы: в такую группу пускает только абонемент,
   * чей товар к ней привязан; заморозка и продление там не действуют.
   */
  isClosedGroup: boolean;
}

export interface Category {
  id: string;
  name: string;
  sortOrder: number;
  isArchived: boolean;
  /** Число активных направлений категории (перекрёстные связи). */
  directionsCount: number;
}

export interface AgeGroup {
  id: string;
  name: string;
  sortOrder: number;
  isArchived: boolean;
  /** Филиалы возрастной группы (age_group_branches). */
  branchIds: string[];
}

export interface Level {
  id: string;
  name: string;
  sortOrder: number;
  isArchived: boolean;
  /** Филиалы уровня (level_branches). */
  branchIds: string[];
}

// --- Directions -------------------------------------------------------------

export interface CreateDirectionBody {
  name: string;
  color?: string;
  sortOrder?: number;
  categoryId?: string | null;
  /** Полностью заменяет филиалы направления (direction_branches). */
  branchIds?: string[];
  /** Полностью заменяет педагогов направления (employee_directions). */
  teacherIds?: string[];
  /** Направление закрытых групп (план 101 §2.5). */
  isClosedGroup?: boolean;
}

export interface UpdateDirectionBody {
  name?: string;
  color?: string;
  sortOrder?: number;
  isArchived?: boolean;
  categoryId?: string | null;
  branchIds?: string[];
  teacherIds?: string[];
  /** Направление закрытых групп (план 101 §2.5). */
  isClosedGroup?: boolean;
}

export function listDirections(params: ListParams = {}): Promise<{ items: Direction[] }> {
  return api.get<{ items: Direction[] }>(`/catalog/directions${archivedQuery(params)}`);
}

export function createDirection(body: CreateDirectionBody): Promise<{ direction: Direction }> {
  return api.post<{ direction: Direction }>(
    '/catalog/directions',
    defined({
      name: body.name,
      color: body.color,
      sort_order: body.sortOrder,
      category_id: body.categoryId,
      branch_ids: body.branchIds,
      teacher_ids: body.teacherIds,
      is_closed_group: body.isClosedGroup,
    }),
  );
}

export function updateDirection(
  id: string,
  body: UpdateDirectionBody,
): Promise<{ direction: Direction }> {
  return api.patch<{ direction: Direction }>(
    `/catalog/directions/${id}`,
    defined({
      name: body.name,
      color: body.color,
      sort_order: body.sortOrder,
      is_archived: body.isArchived,
      category_id: body.categoryId,
      branch_ids: body.branchIds,
      teacher_ids: body.teacherIds,
      is_closed_group: body.isClosedGroup,
    }),
  );
}

export function archiveDirection(id: string): Promise<void> {
  return api.delete<void>(`/catalog/directions/${id}`);
}

// --- Groups (Каталог: категория → направление → ГРУППА → занятия) ------------
// schedule-модуль. GET /schedule/groups отдаёт snake_case items → мапим в camelCase.
// Группа связана с направлением (direction_id); направление — с категорией.

export type GroupType =
  | 'regular'
  | 'personal'
  | 'wedding'
  | 'masterclass'
  | 'rental'
  | 'self_training';
export type GroupStatus = 'active' | 'paused' | 'archived';

export interface ScheduleGroup {
  id: string;
  name: string;
  type: GroupType;
  durationMinutes: number;
  capacity: number | null;
  description: string | null;
  /** Пометка в афише («С ОКТЯБРЯ»), план 145. */
  posterNote: string | null;
  videoUrl: string | null;
  status: GroupStatus;
  directionId: string | null;
  directionName: string | null;
  directionColor: string | null;
  categoryId: string | null;
  categoryName: string | null;
  levelId: string | null;
  levelName: string | null;
  ageGroupId: string | null;
  ageGroupName: string | null;
  branchIds: string[];
  teacherIds: string[];
  /**
   * Различимость одноимённых групп в справочнике (правка владельца 04.09):
   * у 65 из 85 активных групп `teacherIds` пуст — педагог фактически стоит у
   * ЗАНЯТИЙ. Приоритет: явная привязка (`teacherIds`) важнее занятий для
   * педагога, а для филиала — наоборот, занятия важнее `group_branches`
   * (`branchIds` — это «где годится идти», а не «где реально идёт»).
   */
  /** Педагоги: явная привязка, иначе — топ-3 по числу занятий за 60 дней и вперёд. */
  effectiveTeacherIds: string[];
  /** «Фамилия Имя» — параллельно effectiveTeacherIds. */
  effectiveTeacherNames: string[];
  /** «вт 18:00, чт 18:00» (МСК); null — занятий в окне нет. */
  scheduleNote: string | null;
  /** Короткие имена филиалов из занятий; нет занятий — из branchIds. */
  branchNames: string[];
}

interface GroupApi {
  id: string;
  name: string;
  type: GroupType;
  duration_minutes: number;
  capacity: number | null;
  description: string | null;
  poster_note: string | null;
  video_url: string | null;
  status: GroupStatus;
  direction_id: string | null;
  direction_name: string | null;
  direction_color: string | null;
  category_id: string | null;
  category_name: string | null;
  level_id: string | null;
  level_name: string | null;
  age_group_id: string | null;
  age_group_name: string | null;
  branch_ids: string[];
  teacher_ids: string[];
  effective_teacher_ids: string[];
  effective_teacher_names: string[];
  schedule_note: string | null;
  branch_names: string[];
}

export interface CreateGroupBody {
  name: string;
  type?: GroupType;
  directionId?: string | null;
  levelId?: string | null;
  ageGroupId?: string | null;
  durationMinutes?: number;
  capacity?: number | null;
  description?: string;
  posterNote?: string;
  branchIds?: string[];
  teacherIds?: string[];
}

export interface UpdateGroupBody {
  name?: string;
  type?: GroupType;
  directionId?: string | null;
  durationMinutes?: number;
  capacity?: number | null;
  description?: string | null;
  posterNote?: string | null;
  status?: GroupStatus;
  levelId?: string | null;
  ageGroupId?: string | null;
  /** Полностью заменяет состав педагогов группы. */
  teacherIds?: string[];
  /** Полностью заменяет состав филиалов группы. */
  branchIds?: string[];
}

function mapGroup(g: GroupApi): ScheduleGroup {
  return {
    id: g.id,
    name: g.name,
    type: g.type,
    durationMinutes: g.duration_minutes,
    capacity: g.capacity,
    description: g.description,
    posterNote: g.poster_note,
    videoUrl: g.video_url,
    status: g.status,
    directionId: g.direction_id,
    directionName: g.direction_name,
    directionColor: g.direction_color,
    categoryId: g.category_id,
    categoryName: g.category_name,
    levelId: g.level_id,
    levelName: g.level_name,
    ageGroupId: g.age_group_id,
    ageGroupName: g.age_group_name,
    branchIds: g.branch_ids,
    teacherIds: g.teacher_ids,
    effectiveTeacherIds: g.effective_teacher_ids,
    effectiveTeacherNames: g.effective_teacher_names,
    scheduleNote: g.schedule_note,
    branchNames: g.branch_names,
  };
}

// Педагоги для дропдаунов — открытый lookup /schedule/teachers (в отличие от
// /employees, требующего boss-право employee:manage).
export interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
}

export async function listTeachers(branchId?: string): Promise<{ items: Teacher[] }> {
  // Фильтр по филиалу — серверный (employee_branches): в форме мурманской
  // группы московские педагоги только мешают (план 104 Б3).
  const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
  const res = await api.get<{
    items: Array<{ id: string; first_name: string; last_name: string }>;
  }>(`/schedule/teachers${qs}`);
  return {
    items: res.items.map((t) => ({ id: t.id, firstName: t.first_name, lastName: t.last_name })),
  };
}

export interface ListGroupsParams {
  directionId?: string;
  includeArchived?: boolean;
  /** Филиал из шапки: чужие группы в выпадашке расписания только мешают. */
  branchId?: string;
}

export async function listGroups(
  params: ListGroupsParams = {},
): Promise<{ items: ScheduleGroup[] }> {
  const sp = new URLSearchParams();
  if (params.directionId) sp.set('direction_id', params.directionId);
  if (params.branchId) sp.set('branch_id', params.branchId);
  // Архив отсекаем НА СЕРВЕРЕ. Раньше брали первые 200 строк и фильтровали
  // архив здесь — но групп в школе 342, из них 287 архивных, и активные просто
  // не попадали в выборку: список молча приходил неполным (правка владельца
  // 13.08). Потолок подняли до 1000: справочник маленький, нужен целиком.
  if (!params.includeArchived) sp.set('status', 'active');
  sp.set('limit', '1000');
  const res = await api.get<{ items: GroupApi[] }>(`/schedule/groups?${sp.toString()}`);
  return { items: res.items.map(mapGroup) };
}

export function createGroup(body: CreateGroupBody): Promise<{ id: string }> {
  return api.post<{ id: string }>(
    '/schedule/groups',
    defined({
      name: body.name,
      type: body.type,
      direction_id: body.directionId,
      level_id: body.levelId,
      age_group_id: body.ageGroupId,
      duration_minutes: body.durationMinutes,
      capacity: body.capacity,
      description: body.description,
      poster_note: body.posterNote,
      branch_ids: body.branchIds,
      teacher_ids: body.teacherIds,
    }),
  );
}

export function updateGroup(id: string, body: UpdateGroupBody): Promise<{ ok: true }> {
  return api.patch<{ ok: true }>(
    `/schedule/groups/${id}`,
    defined({
      name: body.name,
      type: body.type,
      direction_id: body.directionId,
      level_id: body.levelId,
      age_group_id: body.ageGroupId,
      duration_minutes: body.durationMinutes,
      capacity: body.capacity,
      description: body.description,
      poster_note: body.posterNote,
      status: body.status,
      teacher_ids: body.teacherIds,
      branch_ids: body.branchIds,
    }),
  );
}

// Создание одного занятия (group + дата/время) — POST /schedule/occurrences.
export interface CreateOccurrenceBody {
  groupId: string;
  branchId: string;
  hallId: string;
  teacherId: string;
  scheduledAt: string; // ISO-8601 c offset
  durationMinutes: number;
  maxAttendees?: number;
}

export function createOccurrence(body: CreateOccurrenceBody): Promise<{ id: string }> {
  return api.post<{ id: string }>(
    '/schedule/occurrences',
    defined({
      group_id: body.groupId,
      branch_id: body.branchId,
      hall_id: body.hallId,
      teacher_id: body.teacherId,
      scheduled_at: body.scheduledAt,
      duration_minutes: body.durationMinutes,
      max_attendees: body.maxAttendees,
    }),
  );
}

export function archiveGroup(id: string): Promise<void> {
  return api.delete<void>(`/schedule/groups/${id}`);
}

// --- Levels -----------------------------------------------------------------

export interface CreateLevelBody {
  name: string;
  sortOrder?: number;
  branchIds?: string[];
}

export interface UpdateLevelBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
  branchIds?: string[];
}

export function listLevels(params: ListParams = {}): Promise<{ items: Level[] }> {
  return api.get<{ items: Level[] }>(`/catalog/levels${archivedQuery(params)}`);
}

export function createLevel(body: CreateLevelBody): Promise<{ level: Level }> {
  return api.post<{ level: Level }>(
    '/catalog/levels',
    defined({ name: body.name, sort_order: body.sortOrder, branch_ids: body.branchIds }),
  );
}

export function updateLevel(id: string, body: UpdateLevelBody): Promise<{ level: Level }> {
  return api.patch<{ level: Level }>(
    `/catalog/levels/${id}`,
    defined({
      name: body.name,
      sort_order: body.sortOrder,
      is_archived: body.isArchived,
      branch_ids: body.branchIds,
    }),
  );
}

export function archiveLevel(id: string): Promise<void> {
  return api.delete<void>(`/catalog/levels/${id}`);
}

// --- Categories (Mobifitness «Направление») ---------------------------------

export interface CreateCategoryBody {
  name: string;
  sortOrder?: number;
}

export interface UpdateCategoryBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
}

export function listCategories(params: ListParams = {}): Promise<{ items: Category[] }> {
  return api.get<{ items: Category[] }>(`/catalog/categories${archivedQuery(params)}`);
}

export function createCategory(body: CreateCategoryBody): Promise<{ category: Category }> {
  return api.post<{ category: Category }>(
    '/catalog/categories',
    defined({ name: body.name, sort_order: body.sortOrder }),
  );
}

export function updateCategory(
  id: string,
  body: UpdateCategoryBody,
): Promise<{ category: Category }> {
  return api.patch<{ category: Category }>(
    `/catalog/categories/${id}`,
    defined({ name: body.name, sort_order: body.sortOrder, is_archived: body.isArchived }),
  );
}

export function archiveCategory(id: string): Promise<void> {
  return api.delete<void>(`/catalog/categories/${id}`);
}

// --- Age groups (Mobifitness «Возраст») -------------------------------------

export interface CreateAgeGroupBody {
  name: string;
  sortOrder?: number;
  branchIds?: string[];
}

export interface UpdateAgeGroupBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
  branchIds?: string[];
}

export function listAgeGroups(params: ListParams = {}): Promise<{ items: AgeGroup[] }> {
  return api.get<{ items: AgeGroup[] }>(`/catalog/age-groups${archivedQuery(params)}`);
}

export function createAgeGroup(body: CreateAgeGroupBody): Promise<{ ageGroup: AgeGroup }> {
  return api.post<{ ageGroup: AgeGroup }>(
    '/catalog/age-groups',
    defined({ name: body.name, sort_order: body.sortOrder, branch_ids: body.branchIds }),
  );
}

export function updateAgeGroup(
  id: string,
  body: UpdateAgeGroupBody,
): Promise<{ ageGroup: AgeGroup }> {
  return api.patch<{ ageGroup: AgeGroup }>(
    `/catalog/age-groups/${id}`,
    defined({
      name: body.name,
      sort_order: body.sortOrder,
      is_archived: body.isArchived,
      branch_ids: body.branchIds,
    }),
  );
}

export function archiveAgeGroup(id: string): Promise<void> {
  return api.delete<void>(`/catalog/age-groups/${id}`);
}
